import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm,
  Tag,
  Card,
  Typography,
  Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiService from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

interface RepresentativePaper {
  _id: string;
  title: string;
  authors: string[];
  journal: string;
  year: number;
  doi?: string;
  abstract: string;
  keywords: string[];
  pdfUrl?: string;
  isHighlighted: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

const RepresentativePaperManagement: React.FC = () => {
  const [papers, setPapers] = useState<RepresentativePaper[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPaper, setEditingPaper] = useState<RepresentativePaper | null>(null);
  const [form] = Form.useForm();

  // 获取代表性论文列表
  const fetchPapers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<RepresentativePaper[]>('representative-papers');
      setPapers(response.data || []);
    } catch (error) {
      message.error('获取代表性论文列表失败');
      console.error('Error fetching papers:', error);
    } finally {
      setLoading(false);
    }
  };

  // 删除论文
  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`representative-papers/${id}`);
      message.success('删除成功');
      fetchPapers();
    } catch (error) {
      message.error('删除失败');
      console.error('Error deleting paper:', error);
    }
  };

  // 切换高亮状态
  const handleToggleHighlight = async (id: string, isHighlighted: boolean) => {
    try {
      await apiService.patch(`representative-papers/${id}`, { isHighlighted });
      message.success('状态更新成功');
      fetchPapers();
    } catch (error) {
      message.error('状态更新失败');
      console.error('Error updating highlight status:', error);
    }
  };

  // 打开编辑模态框
  const openEditModal = (paper?: RepresentativePaper) => {
    if (paper) {
      setEditingPaper(paper);
      // 延迟设置表单值，确保数据正确反显
      setTimeout(() => {
        form.setFieldsValue({
          ...paper,
          authors: paper.authors.join(', '),
          keywords: paper.keywords.join(', ')
        });
      }, 100);
    } else {
      setEditingPaper(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 创建或更新论文
  const handleSubmit = async (values: any) => {
    try {
      // 处理作者和关键词数组
      const processedValues = {
        ...values,
        authors: values.authors.split(',').map((author: string) => author.trim()),
        keywords: values.keywords.split(',').map((keyword: string) => keyword.trim())
      };

      if (editingPaper) {
        await apiService.put(`representative-papers/${editingPaper._id}`, processedValues);
        message.success('论文更新成功');
      } else {
        await apiService.post('representative-papers', processedValues);
        message.success('论文创建成功');
      }
      setModalVisible(false);
      setEditingPaper(null);
      form.resetFields();
      fetchPapers();
    } catch (error) {
      message.error(editingPaper ? '更新失败' : '创建失败');
      console.error('Error saving paper:', error);
    }
  };

  useEffect(() => {
    fetchPapers();
  }, []);

  const columns = [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
      width: 70,
      sorter: (a: RepresentativePaper, b: RepresentativePaper) => a.order - b.order,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 250,
      render: (text: string) => (
        <div style={{ fontWeight: 'bold' }} title={text}>
          {text}
        </div>
      ),
    },
    {
      title: '作者',
      dataIndex: 'authors',
      key: 'authors',
      width: 180,
      ellipsis: true,
      render: (authors: string[]) => (
        <div>
          {authors.slice(0, 2).map((author, index) => (
            <Tag key={index} color="blue" style={{ marginBottom: 2 }}>
              {author}
            </Tag>
          ))}
          {authors.length > 2 && <Tag color="default">+{authors.length - 2}</Tag>}
        </div>
      ),
    },
    {
      title: '期刊',
      dataIndex: 'journal',
      key: 'journal',
      ellipsis: true,
      width: 120,
    },
    {
      title: '年份',
      dataIndex: 'year',
      key: 'year',
      width: 70,
      sorter: (a: RepresentativePaper, b: RepresentativePaper) => a.year - b.year,
    },
    {
      title: '高亮',
      dataIndex: 'isHighlighted',
      key: 'isHighlighted',
      width: 80,
      render: (isHighlighted: boolean, record: RepresentativePaper) => (
        <Switch
          checked={isHighlighted}
          onChange={(checked) => handleToggleHighlight(record._id, checked)}
          size="small"
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: RepresentativePaper) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这篇论文吗？"
            onConfirm={() => handleDelete(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0 }}>
            代表性论文管理
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditModal()}
          >
            添加论文
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={papers}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 篇论文`,
          }}
        />

        <Modal
          title={editingPaper ? '编辑代表性论文' : '添加代表性论文'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingPaper(null);
            form.resetFields();
          }}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              year: new Date().getFullYear(),
              isHighlighted: false,
              order: 1,
            }}
          >
            <Form.Item
              name="title"
              label="论文标题"
              rules={[{ required: true, message: '请输入论文标题' }]}
            >
              <Input placeholder="请输入论文标题" />
            </Form.Item>

            <Form.Item
              name="authors"
              label="作者"
              rules={[{ required: true, message: '请输入作者姓名，多个作者用逗号分隔' }]}
            >
              <Input placeholder="请输入作者姓名，多个作者用逗号分隔" />
            </Form.Item>

            <Form.Item
              name="journal"
              label="期刊名称"
              rules={[{ required: true, message: '请输入期刊名称' }]}
            >
              <Input placeholder="请输入期刊名称" />
            </Form.Item>

            <Form.Item
              name="year"
              label="发表年份"
              rules={[{ required: true, message: '请输入发表年份' }]}
            >
              <Input type="number" placeholder="请输入发表年份" />
            </Form.Item>

            <Form.Item
              name="doi"
              label="DOI"
            >
              <Input placeholder="请输入DOI（可选）" />
            </Form.Item>

            <Form.Item
              name="abstract"
              label="摘要"
              rules={[{ required: true, message: '请输入论文摘要' }]}
            >
              <TextArea
                rows={4}
                placeholder="请输入论文摘要"
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="keywords"
              label="关键词"
              rules={[{ required: true, message: '请输入关键词，多个关键词用逗号分隔' }]}
            >
              <Input placeholder="请输入关键词，多个关键词用逗号分隔" />
            </Form.Item>

            <Form.Item
              name="pdfUrl"
              label="PDF文件URL"
            >
              <Input placeholder="请输入PDF文件URL（可选）" />
            </Form.Item>

            <Form.Item
              name="order"
              label="显示顺序"
              rules={[{ required: true, message: '请输入显示顺序' }]}
            >
              <Input type="number" placeholder="数字越小越靠前" />
            </Form.Item>

            <Form.Item
              name="isHighlighted"
              label="高亮显示"
              valuePropName="checked"
            >
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setModalVisible(false);
                  setEditingPaper(null);
                  form.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingPaper ? '更新' : '创建'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default RepresentativePaperManagement;
